import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';
import useReportActiveStatDayGraph from 'hooks/communication/useReportActiveStatDayGraph';
import React from 'react';
import Recoil from 'recoil';
import * as AppStore from 'stores/AppStore';
import useRect from 'hooks/common/useRect';


import { Chart } from 'chart.js';
import * as Luxon from 'luxon';
import { luxonClone } from 'utils/time';
import useSetChartData from 'hooks/app/useSetChartData';

const config = (dataLabels, list, colors, axisName, unit, max, current, stepSize, drawLine, maxTime, onComplete, duration) => ({
  type: 'bar',
  data: {
    labels: dataLabels,
    datasets: [
      {
        maxBarThickness: 30,
        borderRadius: 10,
        data: dataLabels.map((_, index) => list[index].value),
        backgroundColor: (context) => {
          const { index } = context;
          const chart = context.chart;
          const { ctx, chartArea } = chart;

          if (!chartArea) return;
          if (list[index]?.threshold && list[index]?.threshold < list[index]?.value) {
            const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
            gradient.addColorStop(1, colors[1]);
            gradient.addColorStop(0, colors[0]);
            return gradient;
          } else {
            return colors[0];
          }
        },
      }, {
        maxBarThickness: 30,
        borderRadius: 10,
        data: dataLabels.map((_, index) => list[index].active_time),
        backgroundColor: '#80808040',
        yAxisID: 'yy',
      }
    ]
  },
  options: {
    devicePixelRatio: 4,
    animation: {
      duration,
      onComplete,
    },
    layout: {
      padding: {
        top: 60,
        left: 30,
        right: 50,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: {
          display: true,
          drawTicks: false,
          drawOnChartArea: true,
        },
        ticks: {
          fontSize: 14,
        },
        border: {
          dash: [4, 4],
        },
      },
      y: {
        border: {
          width: 1,
          color: '#eeeeee',
        },
        grid: {
          drawTicks: false,
          drawOnChartArea: true,
        },
        min: 0,
        max,
        display: true,
        ticks: {
          fontSize: 11,
          color: '#aaaaaa',
          stepSize,
        },
      },
      yy: {
        border: {
          width: 1,
          color: '#eeeeee',
        },
        grid: {
          display: false,
          drawTicks: false,
          drawOnChartArea: true,
        },
        min: 0,
        max: maxTime,
        display: true,
        position: 'right',
        ticks: {
          fontSize: 11,
          color: '#aaaaaa',
          stepSize: 200,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
  },
  plugins: [{
    afterDatasetDraw: (chart, args, options) => {
      const ctx = chart.ctx;
      const yAxis = chart.scales.y;
      const xAxis = chart.scales.x;

      const meta = args.meta;
      const dataset = meta.controller.getDataset();
      if (!dataset) return;

      if (!yAxis) return;
      if (!xAxis) return;

      const left = xAxis.left;
      const right = xAxis.right;

      ctx.save();
      if (drawLine) {


        ctx.font = '11px Pretendard';
        ctx.fillStyle = '#aaaaaa';

        const y = yAxis.getPixelForValue(max);
        const y5000 = yAxis.getPixelForValue(current);

        ctx.fillStyle = '#aaaaaa';

        ctx.textAlign = 'right';
        ctx.textBaseline = 'bottom';
        ctx.fillText(axisName, left, y - 30);
        ctx.fillText(T('({0})', unit), left, y - 13);

        ctx.textAlign = 'left';
        ctx.textBaseline = 'bottom';
        ctx.fillText(T('활동시간'), right, y - 30);
        ctx.fillText(T('(분)'), right, y - 13);

        const text = T('{0}{1}', current, unit);
        const metrics = ctx.measureText(text)
        const { width } = metrics;
        const fontHeight = metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent;
        const height = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;

        ctx.strokeStyle = '#16B464';
        ctx.beginPath()
        ctx.moveTo(left - 5, y5000);
        ctx.lineTo(right, y5000);
        ctx.stroke();

        ctx.fillStyle = '#16B464';
        ctx.fillRect(left - 10 - width + 1, y5000 - height * 0.5 - 2, width + 1, height + 3);
        ctx.beginPath()
        ctx.moveTo(left - 4, y5000);
        ctx.lineTo(left - 8, y5000 - height * 0.5 - 2);
        ctx.lineTo(left - 8, y5000 + height * 0.5 + 1);
        ctx.lineTo(left - 4, y5000);
        ctx.fill();


        ctx.fillStyle = '#ffffff';
        ctx.font = '10px Pretendard';
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, left - 10, y5000);

      } else {

        ctx.font = '11px Pretendard';
        ctx.fillStyle = '#aaaaaa';

        const y = yAxis.getPixelForValue(max);
        const y200 = yAxis.getPixelForValue(current);

        ctx.textAlign = 'right';
        ctx.textBaseline = 'bottom';
        ctx.fillText(axisName, left, y - 30);
        ctx.fillText(T('({0})', unit), left, y - 13);

        ctx.textAlign = 'left';
        ctx.textBaseline = 'bottom';
        ctx.fillText(T('활동시간'), right, y - 30);
        ctx.fillText(T('(분)'), right, y - 13);

        const text = T('{0}{1}', current, unit);
        const metrics = ctx.measureText(text)
        const { width } = metrics;
        const fontHeight = metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent;
        const height = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;

        ctx.strokeStyle = '#16B464';
        ctx.beginPath()
        ctx.moveTo(left - 5, y200);
        ctx.lineTo(right, y200);
        ctx.stroke();

        ctx.fillStyle = '#16B464';
        ctx.fillRect(left - 10 - width + 1, y200 - height * 0.5 - 2, width + 1, height + 3);
        ctx.beginPath()
        ctx.moveTo(left - 4, y200);
        ctx.lineTo(left - 8, y200 - height * 0.5 - 2);
        ctx.lineTo(left - 8, y200 + height * 0.5 + 1);
        ctx.lineTo(left - 4, y200);
        ctx.fill();

        ctx.fillStyle = '#ffffff';
        ctx.font = '10px Pretendard';
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, left - 10, y200);
      }
      ctx.restore();
    }
  }]
});

export default function ExerciseStatisticsChart(props) {
  const {
    viewMode,
    duration,
    data,
    ...rest
  } = props;

  const setChartData1 = useSetChartData('StepStatisticsChart');
  const setChartData2 = useSetChartData('CaloryStatisticsChart');
  const setChartData = React.useMemo(() => {
    if (viewMode === 'ReportExerciseSteps') {
      return setChartData1;
    } else {
      return setChartData2;
    }
  }, [setChartData1, setChartData2, viewMode]);

  const chartDataTimeoutRef = React.useRef();
  const onComplete = React.useCallback((animation) => {
    if (data.uninitialized) return;
    if (duration) return;
    if (!animation.chart.canvas.width) return;
    if (!animation.chart.canvas.height) return;
    clearTimeout(chartDataTimeoutRef.current);
    const url = animation.chart.canvas.toDataURL('image/png');
    const width = animation.chart.canvas.width;
    const height = animation.chart.canvas.height;
    chartDataTimeoutRef.current = setTimeout(() => {
      setChartData && setChartData(() => ({ url, width, height }));
      URL.revokeObjectURL(url);
    }, 6000);
  }, [data.uninitialized, duration, setChartData]);

  const {
    requestReportActiveStatDayGraph,
  } = useReportActiveStatDayGraph();

  const [list, setList] = React.useState([]);

  const period_start = Recoil.useRecoilValue(AppStore.assets.period_start);
  const period_end = Recoil.useRecoilValue(AppStore.assets.period_end);
  const patient_no = Recoil.useRecoilValue(AppStore.assets.patient_no);

  React.useEffect(() => {
    requestReportActiveStatDayGraph({
      patient_no,
      search_start_time: period_start,
      search_end_time: period_end,
    }, (success, data) => {
      if (success) {
        setList(() => data.daily_active_all_list)
      }
    })
  }, [patient_no, period_end, period_start, requestReportActiveStatDayGraph]);

  const ref = React.useRef();
  const rect = useRect(ref);
  const canvasRef = React.useRef();
  const chartRef = React.useRef();
  React.useEffect(() => {
    const dataLabels = [];
    const values = [];
    let maxTime = 1000;
    if (viewMode === 'ReportExerciseSteps') {
      let max = 10000;
      list.forEach((item) => {
        const luxon = Luxon.DateTime.fromFormat(item.date, 'yyyyMMdd');
        dataLabels.push(luxon.toFormat('M/d'));
        if (max < item.step_cnt) max = item.step_cnt;
        if (maxTime < item.active_time) maxTime = item.active_time;
        values.push({ value: item.step_cnt, threshold: 5000, active_time: item.active_time });
      });
      const chart = new Chart(canvasRef.current, config(dataLabels, values, ['#5f9fff', '#17b467'], '걸음수', '보', Math.ceil(max / 1000) * 1000 + 1000, 5000, 1000, true, Math.ceil(maxTime / 200) * 200 + 200, onComplete, duration));
      chartRef.current = chart;
    } else if (viewMode === 'ReportExerciseCalories') {
      let max = 200;
      list.forEach((item) => {
        const luxon = Luxon.DateTime.fromFormat(item.date, 'yyyyMMdd');
        dataLabels.push(luxon.toFormat('M/d'));
        if (max < item.burned_calorie) max = item.burned_calorie;
        if (maxTime < item.active_time) maxTime = item.active_time;
        values.push({ value: item.burned_calorie, active_time: item.active_time });
      });
      const chart = new Chart(canvasRef.current, config(dataLabels, values, ['#5f9fff'], '소모칼로리', 'kcal', Math.ceil(max / 100) * 100, 200, 100, false, Math.ceil(maxTime / 200) * 200 + 200, onComplete, duration));
      chartRef.current = chart;
    }
    return () => {
      chartRef.current.destroy();
      delete chartRef.current.config;
      chartRef.current = null;
    };
  }, [list, onComplete, viewMode, duration]);

  React.useLayoutEffect(() => {
    if (!chartRef.current) return;
    const frameId = requestAnimationFrame(() => {
      chartRef.current.update();
    });
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [rect]);

  return (
    <div {...ex('ExerciseStatisticsChart', rest)}>
      <div {...ex('Chart', { ref })}>
        <canvas
          {...{
            ref: canvasRef,
            width: rect.width,
            height: rect.height,
          }}
        />
      </div>
    </div>
  );
}
